import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';

interface AdminRouteProps {
  children: React.ReactNode;
  requiredPermission?: string; // Specific permission required for this route
  requireSuperAdmin?: boolean; // If true, only SuperAdmin can access
}

const AdminRoute: React.FC<AdminRouteProps> = ({ 
  children, 
  requiredPermission,
  requireSuperAdmin = false 
}) => {
  const { isAuthenticated, isLoading, hasPermission, isSuperAdmin } = useAuth();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--accent-secondary)',
            borderTop: '3px solid var(--accent-primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  // Check for SuperAdmin requirement
  if (requireSuperAdmin && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check for specific permission requirement
  if (requiredPermission && !hasPermission(requiredPermission) && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user has any admin access (fallback for routes without specific permissions)
  if (!requiredPermission && !requireSuperAdmin) {
    const hasAnyAdminAccess = hasPermission('users_view') || 
                             hasPermission('role_management') ||
                             hasPermission('leads_view') ||
                             hasPermission('custom_monitoring_view') ||
                             hasPermission('alerts_view') ||
                             hasPermission('reports_view') ||
                             hasPermission('blogs_view') ||
                             isSuperAdmin();

    if (!hasAnyAdminAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User has required permissions, allow access
  return <>{children}</>;
};

export default AdminRoute;
